import { useApiMutation } from "@/packages/features-shared";
import type { AuditId, DepthTier } from "@/packages/types";
import { cn, useTranslation } from "@/packages/utils";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { api } from "convex/_generated/api";
import { useState } from "react";
import { Check, Layers } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

// ─── Constants ──────────────────────────────────────────────────────────────

const TIER_ORDER: DepthTier[] = ["basic", "standard", "deep-dive"];

const TIER_COLORS: Record<DepthTier, string> = {
  basic: "bg-emerald-400/10 text-emerald-500 border-emerald-400/20",
  standard: "bg-blue-400/10 text-blue-500 border-blue-400/20",
  "deep-dive": "bg-purple-400/10 text-purple-500 border-purple-400/20",
};

// ─── Props ──────────────────────────────────────────────────────────────────

interface DepthTierSettingProps {
  auditId: AuditId;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function DepthTierSetting({ auditId }: DepthTierSettingProps) {
  const { t } = useTranslation("audits");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingTier, setPendingTier] = useState<DepthTier | null>(null);

  const { data: currentTier } = useWorkspaceQuery(
    api.audit_depth_tier_mutations.getCurrentTier,
    { auditId },
  );

  const { data: preview } = useWorkspaceQuery(
    api.audit_depth_tier_mutations.previewTierChange,
    pendingTier ? { auditId, newTier: pendingTier } : "skip",
  );

  const changeTierMutation = useApiMutation(
    api.audit_depth_tier_mutations.changeDepthTier,
    {
      name: "change-depth-tier",
    },
  );

  if (!currentTier) return null;

  const handleOpenDialog = () => {
    setPendingTier(null);
    setDialogOpen(true);
  };

  const handleSelectTier = (tier: DepthTier) => {
    setPendingTier(tier);
  };

  const handleConfirm = async () => {
    if (!pendingTier) return;
    await changeTierMutation.mutateAsync({
      auditId,
      newTier: pendingTier,
    });
    setDialogOpen(false);
    setPendingTier(null);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setPendingTier(null);
  };

  const tierName = t(`depthTier.tiers.${currentTier}.name`);

  return (
    <>
      {/* Inline display in metadata card */}
      <div className="flex items-center gap-1.5">
        <Layers size={11} className="text-muted shrink-0" />
        <span
          className={cn(
            "text-[11px] px-1.5 py-0.5 rounded border",
            TIER_COLORS[currentTier],
          )}
        >
          {tierName}
        </span>
        <button
          type="button"
          onClick={handleOpenDialog}
          className="text-[10px] text-muted hover:text-accent transition-colors ml-auto"
        >
          {t("depthTier.change")}
        </button>
      </div>

      {/* Change dialog */}
      <ConfirmDialog open={dialogOpen} onClose={handleClose}>
        <ConfirmDialog.Title>{t("depthTier.change")}</ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <div className="flex flex-col gap-4">
            {/* Current tier indicator */}
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-muted">
                {t("depthTier.current")}:
              </span>
              <span
                className={cn(
                  "text-[11px] px-2 py-0.5 rounded border font-medium",
                  TIER_COLORS[currentTier],
                )}
              >
                {tierName}
              </span>
            </div>

            {/* Tier selection */}
            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium text-primary">
                {t("depthTier.selectTier")}
              </span>
              <div className="grid gap-1.5">
                {TIER_ORDER.map((tier) => {
                  const isSelected = pendingTier === tier;
                  const isCurrent = currentTier === tier;
                  return (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => handleSelectTier(tier)}
                      className={cn(
                        "relative flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all duration-150",
                        isSelected
                          ? "border-accent bg-accent/5 ring-1 ring-accent/30"
                          : "border-border hover:border-accent/40",
                        isCurrent && !isSelected && "opacity-60",
                      )}
                    >
                      {/* Radio indicator */}
                      <div
                        className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                          isSelected
                            ? "border-accent bg-accent"
                            : "border-border",
                        )}
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={springs.bouncy}
                          >
                            <Check className="h-2.5 w-2.5 text-white" />
                          </motion.div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "text-[12px] font-semibold",
                              isSelected ? "text-accent" : "text-primary",
                            )}
                          >
                            {t(`depthTier.tiers.${tier}.name`)}
                          </span>
                          {isCurrent && (
                            <span className="rounded-full bg-muted/10 px-2 py-0.5 text-[10px] font-medium text-muted">
                              {t("depthTier.current")}
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-muted">
                          {t(`depthTier.tiers.${tier}.description`)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Preview */}
            <AnimatePresence mode="wait">
              {pendingTier && preview && (
                <motion.div
                  key={preview.type}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="rounded-lg border border-border bg-card-hover px-3 py-2.5"
                >
                  {preview.type === "upgrade" && (
                    <p className="text-[12px] text-secondary">
                      {t("depthTier.upgradePreview", {
                        count: preview.newlyVisibleCount,
                        categories: preview.categoryCount,
                      })}
                    </p>
                  )}
                  {preview.type === "downgrade" && (
                    <div className="flex flex-col gap-1">
                      <p className="text-[12px] text-secondary">
                        {t("depthTier.downgradePreview", {
                          count: preview.hiddenCount,
                          preserved: preview.preservedCount,
                        })}
                      </p>
                      {preview.preservedCount > 0 && (
                        <p className="text-[11px] text-muted">
                          {t("depthTier.downgradeNote")}
                        </p>
                      )}
                    </div>
                  )}
                  {preview.type === "same" && (
                    <p className="text-[12px] text-muted">
                      {t("depthTier.noChange")}
                    </p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleConfirm}
          confirmLabel={
            pendingTier
              ? t("depthTier.confirm", {
                  tier: t(`depthTier.tiers.${pendingTier}.name`),
                })
              : t("depthTier.change")
          }
          confirmDisabled={
            !pendingTier ||
            pendingTier === currentTier ||
            changeTierMutation.isPending
          }
          loading={changeTierMutation.isPending}
        />
      </ConfirmDialog>
    </>
  );
}
